import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { getInventoryallproducts } from "../../API  Functions/InventoryFunctions";
import axiosInstance from "../../AxiosInstance";
import { toast } from "react-toastify";
import { URLS } from "../../API/API endpoints";
import { Breadcrumb } from "antd";
import { DoubleRightOutlined, DownloadOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { routes } from "../../Routes";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/Navbar/Navbar";

/* ── Debounce hook ── */
const useDebounce = (val, ms) => {
  const [d, setD] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setD(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return d;
};

const REASONS = [
  {
    id: "Damaged",
    label: "Damaged",
    desc: "Expired, broken, leaked or unsellable",
    icon: "🛡️",
    color: "#FEF2F2",
    border: "#FECACA",
    accent: "#EF4444",
  },
  {
    id: "Missing",
    label: "Missing",
    desc: "Lost, misplaced, theft or not found",
    icon: "🔍",
    color: "#FFFBEB",
    border: "#FDE68A",
    accent: "#F59E0B",
  },
  {
    id: "Found Extra",
    label: "Found Extra",
    desc: "Extra stock found not in system",
    icon: "📦",
    color: "#F0FDF4",
    border: "#BBF7D0",
    accent: "#10B981",
  },
];

/* ── CSS-in-JS styles object ── */
const S = {
  page: {
    minHeight: "100vh",
    background: "#F1F5F9",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    padding: "0 32px",
    height: 60,
    display: "flex",
    alignItems: "center",
    gap: 16,
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  headerLogo: {
    width: 32,
    height: 32,
    background: "#2563EB",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    flexShrink: 0,
  },
  headerTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: "#0F172A",
    letterSpacing: "-0.01em",
  },
  headerSub: {
    margin: 0,
    fontSize: 12,
    color: "#94A3B8",
  },
  badge: {
    marginLeft: "auto",
    background: "#EFF6FF",
    color: "#2563EB",
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 20,
    padding: "4px 12px",
    border: "1px solid #BFDBFE",
  },
  layout: {
    flex: 1,
    display: "grid",
    gridTemplateColumns: "1fr 420px",
    gap: 0,
    maxWidth: 1280,
    width: "100%",
    margin: "0 auto",
    padding: "28px 24px",
    boxSizing: "border-box",
    alignItems: "start",
  },
  leftPanel: {
    paddingRight: 24,
  },
  rightPanel: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    overflow: "hidden",
    position: "sticky",
    top: 88,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#94A3B8",
    marginBottom: 10,
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    marginBottom: 20,
  },
  cardHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #F1F5F9",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  cardBody: {
    padding: "20px",
  },
};

/* ── Adjustment Panel ── */
function AdjustmentPanel({ product, onCancel, onAdd }) {
  const systemQty = parseInt(product.system_quantity ?? product.quantity ?? 0);
  const [actualQty, setActualQty] = useState(systemQty);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const diff = actualQty - systemQty;
  const diffColor = diff === 0 ? "#64748B" : diff > 0 ? "#10B981" : "#EF4444";
  const diffBg = diff === 0 ? "#F8FAFC" : diff > 0 ? "#F0FDF4" : "#FEF2F2";
  const diffBorder = diff === 0 ? "#E2E8F0" : diff > 0 ? "#BBF7D0" : "#FECACA";

  const handleSubmit = (closeAfter = false) => {
    setSubmitted(true);
    if (diff !== 0 && !reason) return;
    onAdd(
      {
        product,
        systemQty,
        actualQty,
        diff,
        reason: diff === 0 ? null : reason,
        notes,
      },
      closeAfter,
    );
  };

  return (
    <div style={S.card}>
      {/* Product header */}
      <div style={S.cardHeader}>
        {product.image_url || product.image_1920 ? (
          <img
            src={
              product.image_url || `data:image/png;base64,${product.image_1920}`
            }
            alt={product.product_name}
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              objectFit: "cover",
              border: "1px solid #F1F5F9",
              flexShrink: 0,
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: "#F1F5F9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            📦
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: 14,
              color: "#0F172A",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {product.product_name || product.name}
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 3,
            }}
          >
            <span style={{ fontSize: 11, color: "#94A3B8" }}>
              {product.machine_barcode || product.barcode || "No barcode"}
            </span>
            {product.categ_id?.[1] && (
              <span
                style={{
                  fontSize: 10,
                  background: "#EEF2FF",
                  color: "#4F46E5",
                  borderRadius: 4,
                  padding: "2px 7px",
                  fontWeight: 600,
                }}
              >
                {product.categ_id[1]}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onCancel}
          style={{
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 8,
            cursor: "pointer",
            color: "#94A3B8",
            fontSize: 14,
            padding: "6px 10px",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Qty row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12,
          }}
        >
          {/* System Qty */}
          <div
            style={{
              background: "#F8FAFC",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #E2E8F0",
            }}
          >
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 11,
                fontWeight: 600,
                color: "#94A3B8",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              System
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 800,
                color: "#2563EB",
                lineHeight: 1,
              }}
            >
              {systemQty}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#94A3B8",
                  marginLeft: 4,
                }}
              >
                pcs
              </span>
            </p>
          </div>

          {/* Actual Qty */}
          <div
            style={{
              background: "#F0FDF4",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #BBF7D0",
            }}
          >
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 11,
                fontWeight: 600,
                color: "#94A3B8",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Actual
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => {
                  setActualQty((q) => Math.max(0, q - 1));
                  setReason('');
                }}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  border: "1px solid #D1FAE5",
                  background: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#374151",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                −
              </button>
              <input
                type="number"
                min="0"
                value={actualQty}
                onChange={(e) =>{
                  setActualQty(Math.max(0, parseInt(e.target.value) || 0));
                  setReason('');
                }}
                style={{
                  flex: 1,
                  width: 0,
                  textAlign: "center",
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#0F172A",
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                }}
              />
              <button
                onClick={() => {
                  setActualQty((q) => q + 1);
                  setReason('');
              }}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  border: "1px solid #D1FAE5",
                  background: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#374151",
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                +
              </button>
            </div>
          </div>

          {/* Difference */}
          <div
            style={{
              background: diffBg,
              borderRadius: 12,
              padding: "14px 16px",
              border: `1px solid ${diffBorder}`,
            }}
          >
            <p
              style={{
                margin: "0 0 6px",
                fontSize: 11,
                fontWeight: 600,
                color: "#94A3B8",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Δ Diff
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 800,
                color: diffColor,
                lineHeight: 1,
              }}
            >
              {diff > 0 ? `+${diff}` : diff}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#94A3B8",
                  marginLeft: 4,
                }}
              >
                pcs
              </span>
            </p>
          </div>
        </div>

        {/* Reason */}
        {diff !== 0 && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#374151",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Reason for Difference
              </p>
              {submitted && !reason && (
                <span
                  style={{ fontSize: 12, color: "#EF4444", fontWeight: 600 }}
                >
                  Required
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {REASONS.map((r) => {
                const isDisabled =
                  (diff > 0 && (r.id === "Damaged" || r.id === "Missing")) ||
                  (diff < 0 && r.id === "Found Extra");

                return (
                  <label
                    key={r.id}
                    style={{
                      flex: "1 1 140px",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: `1.5px solid ${reason === r.id ? r.accent : "#E2E8F0"}`,
                      background: reason === r.id ? r.color : "#fff",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.5 : 1,
                      transition: "all 0.12s",
                    }}
                  >
                    <input
                      type="radio"
                      name={`reason-${product.quant_id}`}
                      value={r.id}
                      checked={reason === r.id}
                      disabled={isDisabled}
                      onChange={() => setReason(r.id)}
                      style={{
                        accentColor: r.accent,
                        width: 14,
                        height: 14,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        flexShrink: 0,
                      }}
                    />

                    <span style={{ fontSize: 16 }}>{r.icon}</span>

                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#0F172A",
                        }}
                      >
                        {r.label}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 11,
                          color: "#64748B",
                        }}
                      >
                        {r.desc}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 12,
              fontWeight: 700,
              color: "#374151",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Notes{" "}
            <span
              style={{
                color: "#94A3B8",
                fontWeight: 400,
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              (optional)
            </span>
          </p>
          <textarea
            value={notes}
            onChange={(e) => {
              if (e.target.value.length <= 100) setNotes(e.target.value);
            }}
            placeholder="Add any notes…"
            rows={2}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid #E2E8F0",
              padding: "10px 12px",
              fontSize: 13,
              color: "#374151",
              resize: "none",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
              background: "#FAFAFA",
            }}
          />
          <p
            style={{
              margin: "2px 0 0",
              textAlign: "right",
              fontSize: 11,
              color: "#94A3B8",
            }}
          >
            {notes.length}/100
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            onClick={onCancel}
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "1px solid #E2E8F0",
              background: "#fff",
              fontSize: 13,
              fontWeight: 600,
              color: "#374151",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Cancel
          </button>
          {/* <button
            onClick={() => handleSubmit(false)}
            style={{
              flex: 1,
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: "#F1F5F9",
              fontSize: 13,
              fontWeight: 700,
              color: "#2563EB",
              cursor: "pointer",
            }}
          >
            Save &amp; Add Next
          </button> */}
          <button
          disabled={reason == ''}
            onClick={() => handleSubmit(true)}
            style={{
              flex: 1,
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: "#2563EB",
              fontSize: 13,
              fontWeight: 700,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Save &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Adjustments Table ── */
function AdjustmentsTable({ adjustments, onRemove }) {
  if (adjustments.length === 0) return null;

  return (
    <div style={S.card}>
      <div style={{ ...S.cardHeader, justifyContent: "space-between" }}>
        <p
          style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0F172A" }}
        >
          Queued 
          <span
            style={{
              marginLeft: 8,
              background: "#F1F5F9",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 10,
              padding: "2px 8px",
            }}
          >
            {adjustments.length}
          </span>
        </p>
      </div>

      {/* Table header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 80px 80px 80px 100px 32px",
          gap: 0,
          padding: "8px 20px",
          borderBottom: "1px solid #F1F5F9",
          background: "#FAFAFA",
        }}
      >
        {["Product", "System", "Actual", "Diff", "Reason", ""].map((h, i) => (
          <p
            key={i}
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 700,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              textAlign: i >= 1 && i <= 3 ? "center" : "left",
            }}
          >
            {h}
          </p>
        ))}
      </div>

      {adjustments.map((item, idx) => {
        const diff = item.diff;
        const diffColor =
          diff === 0 ? "#64748B" : diff > 0 ? "#10B981" : "#EF4444";
        const reasonLabel = REASONS.find((r) => r.id === item.reason)?.label;
        const reasonIcon = REASONS.find((r) => r.id === item.reason)?.icon;

        return (
          <div
            key={item.product.quant_id}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 80px 80px 80px 100px 32px",
              gap: 0,
              padding: "12px 20px",
              borderBottom: "1px solid #F8FAFC",
              alignItems: "center",
              transition: "background 0.1s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFC")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  margin: 0,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#0F172A",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.product.product_name || item.product.name}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: "#94A3B8" }}>
                {item.product.machine_barcode || item.product.barcode || "—"}
              </p>
            </div>
            <p
              style={{
                margin: 0,
                textAlign: "center",
                fontSize: 13,
                fontWeight: 600,
                color: "#64748B",
              }}
            >
              {item.systemQty}
            </p>
            <p
              style={{
                margin: 0,
                textAlign: "center",
                fontSize: 13,
                fontWeight: 600,
                color: "#0F172A",
              }}
            >
              {item.actualQty}
            </p>
            <p
              style={{
                margin: 0,
                textAlign: "center",
                fontSize: 13,
                fontWeight: 800,
                color: diffColor,
              }}
            >
              {diff > 0 ? `+${diff}` : diff}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {reasonLabel ? (
                <span
                  style={{
                    fontSize: 12,
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span>{reasonIcon}</span> {reasonLabel}
                </span>
              ) : (
                <span style={{ fontSize: 12, color: "#94A3B8" }}>—</span>
              )}
            </div>
            <button
              onClick={() => onRemove(idx)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#CBD5E1",
                fontSize: 14,
                padding: 4,
                lineHeight: 1,
                borderRadius: 6,
                transition: "color 0.1s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#EF4444")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#CBD5E1")}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════
   Main Page
════════════════════════════════════════ */
export default function StockAdjustmentPage() {
  const user = useSelector((s) => s.login?.user?.result);
  const storeId = user?.company_id;
  const userId = user?.user_id;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 350);

  const [activeProduct, setActiveProduct] = useState(null);
  const [adjustments, setAdjustments] = useState([]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const dropdownRef = useRef(null);
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);

  /* ── Fetch search results ── */
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    (async () => {
      setSearchLoading(true);
      try {
        const data = await getInventoryallproducts(
          userId,
          storeId,
          undefined,
          undefined,
          debouncedSearch,
          10,
          0,
          undefined,
          false,
        );
        if (data?.result[0]?.status_code == 200) {
          const products = data?.result[0]?.response || data?.result || [];
          setSearchResults(products);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(true);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    })();
  }, [debouncedSearch, userId, storeId]);

  useEffect(() => {
    const h = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchWrapRef.current &&
        !searchWrapRef.current.contains(e.target)
      )
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSelectProduct = (p) => {
    setActiveProduct(p);
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handleAdd = (entry, closeAfter = false) => {
    setAdjustments((prev) => {
      const idx = prev.findIndex(
        (a) => a.product.quant_id === entry.product.quant_id,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = entry;
        return next;
      }
      return [...prev, entry];
    });
    setActiveProduct(null);
    if (!closeAfter) setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleRemove = (idx) =>
    setAdjustments((prev) => prev.filter((_, i) => i !== idx));

  const handleFinalSubmit = async () => {
    if (adjustments.length === 0) return;
    const items = adjustments.map((entry) => ({
      product_id: entry.product.quant_id,
      system_qty: entry.systemQty,
      actual_qty: entry.actualQty,
      difference_qty: Math.abs(entry.diff),
      reason: entry.reason ?? "",
      notes: entry.notes ?? "",
    }));
    const payload = { company_id: storeId, user_id: userId, items };
    setSubmitLoading(true);
    try {
      const response = await axiosInstance.post(
        URLS.CREATE_INVENTORY_STOCK_VALIDATION,
        payload,
      );
      const result = response?.data?.result;
      if (result?.status_code === 200 || result?.status === "success") {
        setSubmitResult(result);
        setSubmitted(true);
        toast.success(
          result?.message || "Inventory verification created successfully",
        );
        navigate(routes.inventory.inventoryVerification);
      } else {
        toast.error(
          result?.status || result?.message || "Failed to submit adjustments",
        );
      }
    } catch (error) {
      console.error("CreateInventoryStockVerification error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const netDiff = adjustments.reduce((s, a) => s + a.diff, 0);
  const gains = adjustments
    .filter((a) => a.diff > 0)
    .reduce((s, a) => s + a.diff, 0);
  const losses = Math.abs(
    adjustments.filter((a) => a.diff < 0).reduce((s, a) => s + a.diff, 0),
  );

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            border: "1px solid #E2E8F0",
            padding: "48px 40px",
            maxWidth: 420,
            width: "100%",
            textAlign: "center",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              background: "#F0FDF4",
              border: "2px solid #BBF7D0",
              borderRadius: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              margin: "0 auto 20px",
            }}
          >
            ✅
          </div>
          <h2
            style={{
              margin: "0 0 8px",
              fontWeight: 800,
              fontSize: 22,
              color: "#0F172A",
              letterSpacing: "-0.02em",
            }}
          >
            Stocks Submitted
          </h2>
          <p style={{ margin: "0 0 6px", color: "#64748B", fontSize: 14 }}>
            {adjustments.length} product
            {adjustments.length !== 1 ? "s" : ""} have been recorded.
          </p>
          {submitResult?.in_stock_ver_id && (
            <p
              style={{
                margin: "0 0 28px",
                color: "#2563EB",
                fontSize: 13,
                fontWeight: 700,
                background: "#EFF6FF",
                borderRadius: 8,
                padding: "8px 16px",
                display: "inline-block",
              }}
            >
              Verification ID: #{submitResult.in_stock_ver_id}
            </p>
          )}
          <button
            onClick={() => {
              setAdjustments([]);
              setSubmitted(false);
              setSubmitResult(null);
            }}
            style={{
              width: "100%",
              padding: "13px",
              borderRadius: 12,
              border: "none",
              background: "#2563EB",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Start New Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type=number] { -moz-appearance: textfield; }

        .search-result-item:hover { background: #F8FAFC !important; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .stock-layout {
            grid-template-columns: 1fr !important;
            padding: 16px !important;
          }
          .stock-left-panel { padding-right: 0 !important; }
          .stock-right-panel {
            position: static !important;
            order: -1;
          }
          .stock-header { padding: 0 16px !important; }
          .qty-grid {
            grid-template-columns: 1fr 1fr 1fr !important;
          }
          .adj-table-row {
            grid-template-columns: 1fr 60px 60px 60px 80px 28px !important;
          }
          .reason-row { flex-direction: column !important; }
        }
        @media (max-width: 480px) {
          .qty-grid {
            grid-template-columns: 1fr 1fr !important;
          }
          .qty-grid > div:last-child { grid-column: span 2; }
          .adj-table-header, .adj-table-row {
            grid-template-columns: 1fr 56px 56px 56px !important;
          }
          .adj-reason-col, .adj-remove-col { display: none !important; }
        }
      `}</style>

      <div style={S.page}>
        {/* Header */}
        <Navbar heading="Stocks" />
        <div className="font-extrabold mx-2">
          <Breadcrumb
            separator={<DoubleRightOutlined />}
            items={[
              {
                title: (
                  <Link to={routes.inventory.inventoryVerification}>
                    {t("Inventory Verification")}
                  </Link>
                ),
                key: "inventory_verification",
              },
              { title: t("Stock") },
            ]}
            className="relative mt-3 font-semibold"
          />
        </div>
        {/* Two-column layout */}
        <div style={S.layout} className="stock-layout">
          {/* ── Left: Search + Panel + Table ── */}
          <div style={S.leftPanel} className="stock-left-panel">
            {/* Search bar */}
            <div style={{ marginBottom: 20 }}>
              <p style={S.sectionLabel}>Add Product</p>
              <div style={{ position: "relative" }} ref={searchWrapRef}>
                <span
                  style={{
                    position: "absolute",
                    left: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94A3B8",
                    fontSize: 16,
                    pointerEvents: "none",
                  }}
                >
                  🔎
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() =>
                    searchResults.length > 0 && setShowDropdown(true)
                  }
                  placeholder="Search by product name or barcode…"
                  autoComplete="off"
                  style={{
                    width: "100%",
                    padding: "12px 40px 12px 42px",
                    borderRadius: 12,
                    border: "1px solid #E2E8F0",
                    background: "#fff",
                    fontSize: 14,
                    color: "#0F172A",
                    outline: "none",
                    boxSizing: "border-box",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    transition: "border-color 0.15s",
                  }}
                  onFocusCapture={(e) =>
                    (e.target.style.borderColor = "#2563EB")
                  }
                  onBlurCapture={(e) =>
                    (e.target.style.borderColor = "#E2E8F0")
                  }
                />
                {searchLoading && (
                  <span
                    style={{
                      position: "absolute",
                      right: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: "2px solid #E2E8F0",
                      borderTopColor: "#2563EB",
                      animation: "spin 0.7s linear infinite",
                      display: "inline-block",
                    }}
                  />
                )}

                {/* Dropdown */}
                {showDropdown && (
                  <ul
                    ref={dropdownRef}
                    style={{
                      position: "absolute",
                      zIndex: 200,
                      top: "calc(100% + 6px)",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #E2E8F0",
                      borderRadius: 12,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
                      maxHeight: 300,
                      overflowY: "auto",
                      margin: 0,
                      padding: 6,
                      listStyle: "none",
                    }}
                  >
                    {searchResults.length > 0 ? (searchResults.map((p, i) => (
                      <li
                        key={p.quant_id || i}
                        className="search-result-item"
                        onMouseDown={() => handleSelectProduct(p)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "9px 12px",
                          borderRadius: 8,
                          cursor: "pointer",
                          background: "transparent",
                          transition: "background 0.1s",
                        }}
                      >
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            background: "#F1F5F9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            flexShrink: 0,
                          }}
                        >
                          📦
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p
                            style={{
                              margin: 0,
                              fontWeight: 600,
                              fontSize: 13,
                              color: "#0F172A",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {p.product_name || p.name}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 11,
                              color: "#94A3B8",
                            }}
                          >
                            {p.machine_barcode || p.barcode || "—"}
                          </p>
                        </div>
                        {p.qty_available !== undefined && (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#2563EB",
                              background: "#EFF6FF",
                              borderRadius: 6,
                              padding: "2px 8px",
                              flexShrink: 0,
                            }}
                          >
                            {p.qty_available} pcs
                          </span>
                        )}
                      </li>
                    ))
                     ) : (
      <li
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          padding: "20px 12px",
          color: "#94A3B8",
          pointerEvents: "none",
        }}
      >
        <span style={{ fontSize: 24 }}>🔍</span>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#64748B" }}>
          No products found
        </p>
        <p style={{ margin: 0, fontSize: 12, color: "#CBD5E1" }}>
          Try a different name or barcode
        </p>
      </li>
    )}
  </ul>
)
                  }
              </div>
            </div>

            {/* Adjustment panel */}
            {activeProduct && (
              <div style={{ marginBottom: 20 }}>
                <p style={S.sectionLabel}>Adjust Quantity</p>
                <AdjustmentPanel
                  key={activeProduct.quant_id}
                  product={activeProduct}
                  onCancel={() => setActiveProduct(null)}
                  onAdd={handleAdd}
                />
              </div>
            )}

            {/* Adjustments table */}
            {adjustments.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <p style={S.sectionLabel}>Queued ({adjustments.length})</p>
                  <button
                    onClick={() => setAdjustments([])}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#EF4444",
                      fontWeight: 600,
                      padding: "2px 0",
                    }}
                  >
                    Clear all
                  </button>
                </div>
                <AdjustmentsTable
                  adjustments={adjustments}
                  onRemove={handleRemove}
                />
              </div>
            )}

            {/* Empty state */}
            {!activeProduct && adjustments.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "80px 24px",
                  color: "#CBD5E1",
                  background: "#fff",
                  borderRadius: 16,
                  border: "1px dashed #E2E8F0",
                }}
              >
                <div style={{ fontSize: 48, marginBottom: 14 }}>📋</div>
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#94A3B8",
                    margin: 0,
                  }}
                >
                  Search for a product to begin
                </p>
              </div>
            )}
          </div>

          {/* ── Right: Summary + Submit ── */}
          <div style={S.rightPanel} className="stock-right-panel">
            <div
              style={{
                padding: "18px 20px",
                borderBottom: "1px solid #F1F5F9",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                Session Summary
              </p>
            </div>

            {adjustments.length === 0 ? (
              <div style={{ padding: "32px 20px", textAlign: "center" }}>
                <p style={{ fontSize: 13, color: "#94A3B8", margin: 0 }}>
                  No stocks yet
                </p>
              </div>
            ) : (
              <div
                style={{
                  padding: "16px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {/* Stats */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      background: "#F8FAFC",
                      borderRadius: 10,
                      padding: "12px 14px",
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 4px",
                        fontSize: 11,
                        color: "#94A3B8",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Products
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 22,
                        fontWeight: 800,
                        color: "#0F172A",
                      }}
                    >
                      {adjustments.length}
                    </p>
                  </div>
                  <div
                    style={{
                      background: netDiff >= 0 ? "#F0FDF4" : "#FEF2F2",
                      borderRadius: 10,
                      padding: "12px 14px",
                      border: `1px solid ${netDiff >= 0 ? "#BBF7D0" : "#FECACA"}`,
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 4px",
                        fontSize: 11,
                        color: "#94A3B8",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Net Diff
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 22,
                        fontWeight: 800,
                        color: netDiff >= 0 ? "#10B981" : "#EF4444",
                      }}
                    >
                      {netDiff > 0 ? `+${netDiff}` : netDiff}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      background: "#F0FDF4",
                      borderRadius: 10,
                      padding: "10px 14px",
                      border: "1px solid #D1FAE5",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 2px",
                        fontSize: 11,
                        color: "#94A3B8",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Gains
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#10B981",
                      }}
                    >
                      +{gains}
                    </p>
                  </div>
                  <div
                    style={{
                      background: "#FEF2F2",
                      borderRadius: 10,
                      padding: "10px 14px",
                      border: "1px solid #FECACA",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 2px",
                        fontSize: 11,
                        color: "#94A3B8",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Losses
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#EF4444",
                      }}
                    >
                      −{losses}
                    </p>
                  </div>
                </div>

                {/* Reason breakdown */}
                <div
                  style={{
                    borderTop: "1px solid #F1F5F9",
                    paddingTop: 12,
                    marginTop: 2,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 8px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    By Reason
                  </p>
                  {REASONS.map((r) => {
                    const count = adjustments.filter(
                      (a) => a.reason === r.id,
                    ).length;
                    if (count === 0) return null;
                    return (
                      <div
                        key={r.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 0",
                          borderBottom: "1px solid #F8FAFC",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            color: "#374151",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span>{r.icon}</span> {r.label}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            background: r.color,
                            color: r.accent,
                            borderRadius: 6,
                            padding: "2px 8px",
                            border: `1px solid ${r.border}`,
                          }}
                        >
                          {count}
                        </span>
                      </div>
                    );
                  })}
                  {adjustments.filter((a) => !a.reason).length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 0",
                      }}
                    >
                      <span style={{ fontSize: 13, color: "#94A3B8" }}>
                        No change
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          background: "#F8FAFC",
                          color: "#64748B",
                          borderRadius: 6,
                          padding: "2px 8px",
                          border: "1px solid #E2E8F0",
                        }}
                      >
                        {adjustments.filter((a) => !a.reason).length}
                      </span>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <button
                  onClick={handleFinalSubmit}
                  disabled={submitLoading}
                  style={{
                    width: "100%",
                    padding: "13px",
                    borderRadius: 12,
                    border: "none",
                    background: submitLoading ? "#93C5FD" : "#1b4597",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 800,
                    cursor: submitLoading ? "not-allowed" : "pointer",
                    boxShadow: submitLoading
                      ? "none"
                      : "0 2px 8px rgba(37,99,235,0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    marginTop: 4,
                    transition: "background 0.2s",
                  }}
                >
                  {submitLoading && (
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        animation: "spin 0.7s linear infinite",
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {submitLoading
                    ? "Submitting…"
                    : `Submit`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
